import {
  Breadcrumb,
  BreadcrumbItem,
  Label,
  Pagination,
  TextInput,
} from "flowbite-react";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { HiHome } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { getUsers } from "../../redux/actionCreators/users";
import ActionType from "../../redux/actionTypes";
import AddUserModal from "./AddUserModal";
import { debounce } from "lodash";
import AllUsersTable from "./AllUsersTable";
import LoadingIcon from "../../components/icons/LoadingIcon";
import ColumnSelector, { type ColumnConfig } from "./ColumnSelector";

export enum UserSortField {
  NAME = "name",
  CREATED_AT = "created_at",
  ACTIVITY_STATUS = "activity_status",
  IDENTITY_PROVIDER = "identity_provider",
  DAILY_AVERAGE_MINUTES_PLAYED = "daily_average_minutes_played",
  TOTAL_MINUTES_PLAYED = "total_minutes_played",
  LAST_LOGIN_TIMESTAMP = "last_login_timestamp",
  STATUS = "status",
  STREAK_DAYS = "streak_days",
  BEST_STREAK = "best_streak",
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

interface RootState {
  auth: any;
  users: any;
}

const MAX_CONTENT_PER_PAGE = 10;

const UserListPage: FC = function () {
  const dispatch: any = useDispatch();
  const state: any = useSelector((state: RootState) => state);

  const [users, setUsers] = useState<{ total: number; data: any[] }>({
    total: 0,
    data: [],
  });
  const [loading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<UserSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { id: UserSortField.CREATED_AT, label: "Creation Date", enabled: true },
    {
      id: UserSortField.ACTIVITY_STATUS,
      label: "Activity Status",
      enabled: false,
    },
    {
      id: UserSortField.IDENTITY_PROVIDER,
      label: "Auth Provider",
      enabled: false,
    },
    {
      id: UserSortField.DAILY_AVERAGE_MINUTES_PLAYED,
      label: "Daily Average Played",
      enabled: false,
    },
    {
      id: UserSortField.TOTAL_MINUTES_PLAYED,
      label: "Total Time Played",
      enabled: false,
    },
    {
      id: UserSortField.LAST_LOGIN_TIMESTAMP,
      label: "Last Login",
      enabled: true,
    },
    { id: UserSortField.STATUS, label: "Status", enabled: true },
    { id: UserSortField.STREAK_DAYS, label: "Streak Days", enabled: true },
    { id: UserSortField.BEST_STREAK, label: "Best Streak", enabled: true },
    { id: "actions" as const, label: "Actions", enabled: true },
  ]);

  // Reference to store the current AbortController (so we can cancel requests)
  const abortControllerRef = useRef<AbortController | null>(null);

  const debouncedSearch = useCallback((value: string) => {
    debounce(() => {
      setSearch(value);
      setCurrentPage(1);
    }, 300)();
  }, []);

  /**
   * getAllUsers
   * Fetches users from the server while canceling any existing request.
   */
  const getAllUsers = useCallback(
    async (
      pageToFetch: number,
      currentSearch: string,
      currentSortField: UserSortField | null,
      currentSortOrder: SortOrder,
    ) => {
      // Cancel any ongoing request before making a new one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new abort controller for the next request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);

      dispatch(
        getUsers({
          page: pageToFetch,
          take: MAX_CONTENT_PER_PAGE,
          search: currentSearch,
          sortField: currentSortField,
          sortOrder: currentSortOrder,
          include_streaks: true,
          signal: abortControllerRef.current.signal,
        }),
      );
    },
    [dispatch],
  );

  /**
   * onPageChange
   * Sets the new page for pagination.
   */
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: UserSortField) => {
    if (sortField === field) {
      if (sortOrder === SortOrder.ASC) {
        setSortOrder(SortOrder.DESC);
      } else {
        setSortField(null);
        setSortOrder(SortOrder.ASC);
      }
    } else {
      setSortField(field);
      setSortOrder(SortOrder.ASC);
    }
    setCurrentPage(1);
  };

  // Trigger fetch whenever currentPage, search, sortField, or sortOrder changes
  useEffect(() => {
    getAllUsers(currentPage, search, sortField, sortOrder);
  }, [currentPage, search, sortField, sortOrder, getAllUsers]);

  // Respond to Redux store updates
  useEffect(() => {
    if (state.users.event === ActionType.GET_ALL_USERS) {
      setUsers(state.users.users);
      setIsLoading(false);
    } else if (state?.users?.type === ActionType.GET_ALL_USERS_FAILED) {
      console.error("Error: unable to fetch users");
      setIsLoading(false);
    }
  }, [state]);

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/users/list">Users</BreadcrumbItem>
              <BreadcrumbItem>List</BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              All users
            </h1>
          </div>
          <div className="sm:flex">
            <div className="mb-3 hidden items-center dark:divide-gray-700 sm:mb-0 sm:flex sm:divide-x sm:divide-gray-100">
              <form className="lg:pr-3">
                <Label htmlFor="users-search" className="sr-only">
                  Search
                </Label>
                <div className="relative mt-1 lg:w-64 xl:w-96">
                  <TextInput
                    id="users-search"
                    name="users-search"
                    placeholder="Search for users"
                    onChange={(e: any) => {
                      // Cancel previous request on new search input
                      debouncedSearch(e.target.value);
                    }}
                  />
                </div>
              </form>
            </div>
            <div className="ml-auto flex items-center space-x-2 sm:space-x-3">
              <ColumnSelector columns={columns} onColumnsChange={setColumns} />
              <AddUserModal
                getUsers={() =>
                  getAllUsers(currentPage, search, sortField, sortOrder)
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div className=" flex h-full flex-col">
        <div className="overflow-x-auto overflow-y-hidden">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow-sm">
              {loading ? (
                <div role="status" className="text-center">
                  <LoadingIcon />
                  <span className="sr-only">Loading...</span>
                </div>
              ) : (
                <div className="h-full overflow-y-hidden pb-4">
                  <AllUsersTable
                    users={users}
                    getUsers={() =>
                      getAllUsers(currentPage, search, sortField, sortOrder)
                    }
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    visibleColumns={columns}
                  />
                  {users?.total !== users?.data?.length && (
                    <Pagination
                      currentPage={currentPage}
                      onPageChange={onPageChange}
                      showIcons
                      aria-disabled={true}
                      totalPages={Math.ceil(
                        Number(users?.total || 1) / MAX_CONTENT_PER_PAGE,
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        rtl={false}
        pauseOnFocusLoss
        pauseOnHover
        theme="dark"
      />
    </NavbarSidebarLayout>
  );
};

export default UserListPage;
